import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Send, Sparkles, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api, { formatError } from '@/lib/api';
import { goalLabel } from '@/lib/campaign';

const EXAMPLES = [
  'I run a bakery in Pune and want more birthday cake orders',
  'New yoga batch starting Monday, I want people to sign up',
  'Grand opening of my salon in Indore this Sunday, 20% off',
];

const PAGE_CHOICE = 'Connect or make my Facebook Page';

/** Chat where the customer describes their ad in plain words. The AI asks short follow-up
 * questions, then hands a suggested brief to onSuggestion. When the ad should be WhatsApp but they have
 * no Facebook Page of their own, the chat asks first: their Page (onPageGuide) or another goal. */
export default function AdChat({ messages, setMessages, onSuggestion, onPageGuide }) {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView?.({ behavior: 'smooth' }); }, [messages, busy]);

  const send = async (content = text) => {
    content = content.trim();
    if (!content || busy) return;
    const next = [...messages, { role: 'user', content }];
    setMessages(next); setText(''); setBusy(true);
    try {
      const { data } = await api.post('/api/campaigns/brief-chat', {
        messages: next.map(({ role, content: c }) => ({ role, content: c })),
      });
      if (data.type === 'suggestion' && data.needs_page) {
        setMessages([...next, { role: 'assistant', kind: 'page', suggestion: data,
          content: "WhatsApp ads need your own Facebook Page, and you haven't connected one yet. What would you like to do?",
          options: [PAGE_CHOICE, ...data.goals.map(goalLabel)] }]);
      } else if (data.type === 'suggestion') {
        setMessages([...next, { role: 'assistant', content: "Got it! I've set up your ad. Let's go through it together, you can change anything." }]);
        onSuggestion(data);
      } else {
        setMessages([...next, { role: 'assistant', content: data.question, kind: data.type, options: data.options || [] }]);
      }
    } catch (err) {
      setMessages(messages); setText(content);
      toast.error(formatError(err.response?.data?.detail) || "I couldn't reply just now. Please send that again.");
      if (err.response?.status === 402) navigate('/pricing'); // no ads left: pick a plan first
    }
    setBusy(false);
  };
  // An answer to "WhatsApp needs your Page": no AI call, the brief is already worked out.
  const choose = (option, s) => {
    const next = [...messages, { role: 'user', content: option }];
    if (option === PAGE_CHOICE) { onPageGuide(s, next); return; }
    const goal = s.goals.find(g => goalLabel(g) === option);
    setMessages([...next, { role: 'assistant', content: "Got it! I've set up your ad. Let's go through it together, you can change anything." }]);
    onSuggestion({ ...s, goal, alternatives: s.alternatives.filter(g => g !== goal) });
  };
  const last = messages[messages.length - 1];

  return (
    <div className="flex flex-col rounded-2xl border-2 border-ink bg-card shadow-neo overflow-hidden" data-testid="ad-chat">
      <div className="flex-1 min-h-[320px] max-h-[55vh] overflow-y-auto p-4 space-y-3">
        <Bubble role="assistant">Hi! Tell me about the ad you want, in your own words. What are you promoting, and what should people do after seeing it?</Bubble>
        {messages.map((m, i) => <Bubble key={i} role={m.role} kind={m.kind}>{m.content}</Bubble>)}
        {!busy && last?.options?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pl-9" data-testid="ad-chat-options">
            {last.options.map(o => (
              <button key={o} onClick={() => (last.kind === 'page' ? choose(o, last.suggestion) : send(o))}
                className="px-3 py-1 rounded-full border-2 border-ink bg-butter text-xs font-semibold hover:bg-brand">{o}</button>
            ))}
          </div>
        )}
        {busy && <Bubble role="assistant"><Loader2 className="w-4 h-4 animate-spin" /></Bubble>}
        <div ref={endRef} />
      </div>
      {!messages.length && (
        <div className="px-4 pb-3 flex flex-wrap gap-1.5">
          {EXAMPLES.map(ex => (
            <button key={ex} onClick={() => setText(ex)} className="px-2.5 py-1 rounded-full border-2 border-ink/25 bg-card text-xs font-medium hover:border-ink hover:bg-butter">{ex}</button>
          ))}
        </div>
      )}
      <div className="border-t-2 border-ink p-3 flex gap-2 items-end">
        <Textarea rows={2} value={text} onChange={e => setText(e.target.value)} placeholder="Describe your ad..."
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          className="resize-none" data-testid="ad-chat-input" />
        <Button className="gradient-bg h-10" onClick={() => send()} disabled={busy || !text.trim()} data-testid="ad-chat-send">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}

function Bubble({ role, kind, children }) {
  if (role === 'user') {
    return <div className="flex justify-end"><div className="max-w-[85%] px-3.5 py-2 rounded-2xl rounded-br-sm gradient-bg text-sm whitespace-pre-wrap break-words">{children}</div></div>;
  }
  const warn = kind === 'blocked';
  return (
    <div className="flex gap-2 items-start">
      <div className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center ${warn ? 'bg-amber-500/10 border-2 border-amber-600' : 'bg-brand border-2 border-ink'}`}>
        {warn ? <ShieldAlert className="w-3.5 h-3.5 text-amber-700 dark:text-amber-400" /> : <Sparkles className="w-3.5 h-3.5 text-[#141414]" />}
      </div>
      <div className={`max-w-[85%] px-3.5 py-2 rounded-2xl rounded-tl-sm text-sm whitespace-pre-wrap break-words min-w-0 ${warn ? 'bg-amber-500/10 border border-amber-500/20 text-amber-900 dark:text-amber-200' : 'bg-butter/70 border-2 border-ink/15'}`}>{children}</div>
    </div>
  );
}
